import React from "react";
import { useLocation } from "react-router-dom";
import { EventContext } from "../../common/contexts/event";
import { useEventContextListener } from "../../common/hooks/useeventcontextlistener";
import { useEventListener } from "../../common/hooks/uselistener";
import { useObservable, useSubscription } from "../../common/hooks/useobservable";
import { IFetchCompleteEvent } from "../../common/utilities/fetch";
import { defer } from "../../common/utilities/promise";
import { getScenarioEvent, startScenario } from "../../common/utilities/scenario";
import { SessionContext } from "../contexts/session";

/**
 * Logs data associated with the user's activity browsing photos.
 */
export function useLogBrowse(): void {
  const eventContext = React.useContext(EventContext);
  const sessionContext = React.useContext(SessionContext);

  const [visible, setVisible] = useObservable(document.visibilityState !== "hidden");
  const visibleValue = useSubscription(visible);

  const countThumbnailFetch = React.useRef(0);
  const countThumbnailFromCache = React.useRef(0);

  const _start = React.useCallback(start, [eventContext, sessionContext]);

  useEventContextListener("fetchComplete", React.useCallback(handleFetchComplete, []));
  useEventListener(document, "visibilitychange", React.useCallback(handleVisibilityChange, [setVisible]));
  const location = useLocation();

  React.useEffect(() => {
    if (visibleValue) {
      return _start();
    }
  }, [
    _start,
    location, // New scenario when user navigates to a different location
    visibleValue
  ]);

  /**
   * Starts a new telemetry scenario.
   * @returns Callback for ending the scenario
   */
  function start(): () => void {
    const { promise, resolve } = defer<void>();

    const scenario = startScenario(
      promise,
      {
        properties: { pivot: sessionContext.getPage().current },
        scenarioName: "browse",
        scenarioType: "useraction"
      },
      (scenarioDetails) => {
        const scenarioEvent = getScenarioEvent("scenarioComplete", scenarioDetails);
        eventContext.dispatchEvent("scenarioComplete", scenarioEvent);
        eventContext.dispatchEvent("telemetryAvailable", scenarioEvent);
      }
    );

    let peakFetches = 0;
    let countAtStart = countThumbnailFetch.current;
    const peakFetchesInterval = window.setInterval(() => {
      const fetchesInSecond = countThumbnailFetch.current - countAtStart;
      peakFetches = Math.max(peakFetches, fetchesInSecond);

      // Reset count for the next interval execution
      countAtStart = countThumbnailFetch.current;
    }, 1000);

    return () => {
      scenario.log({
        thumbnailFetchCount: countThumbnailFetch.current,
        thumbnailFromCacheCount: countThumbnailFromCache.current,
        peakFetchesPerSecond: peakFetches
      });

      countThumbnailFetch.current = 0;
      countThumbnailFromCache.current = 0;

      clearInterval(peakFetchesInterval);

      resolve();
    };
  }

  function handleFetchComplete(event: IFetchCompleteEvent): void {
    if (event.name === "downloadPhoto") {
      countThumbnailFetch.current++;
    } else if (event.name === "cachedDownload") {
      countThumbnailFromCache.current++;
    }
  }

  function handleVisibilityChange(): void {
    setVisible(document.visibilityState !== "hidden");
  }
}
